

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import qs from 'qs';
import { frontendLogger } from '../utils/logger';
import { getTenantLockout, isEmailProviderAuthFailure } from '../utils/apiErrors';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { buildAppLoginUrl, consumeSignedOutFlag } from '../utils/tenantUrl';
import {
  clearAccessToken,
  clearLegacyTokenStorage,
  getAccessToken,
  setAccessToken,
} from './tokenStore';
import {previewPrivateObject} from "../utils/S3";
import { getBrowserTimezone } from "../utils/userTimezone";

const AuthContext = createContext(null);

const AUTH_HANDOFF_HASH_PREFIX = '#auth=';

const shouldRedirectToTenantSubdomain = (schema, hostname) => {
  if (!schema || hostname.startsWith(`${schema}.`)) {
    return false;
  }
  return true;
};

const consumeAuthHandoffFromHash = () => {
  const { hash } = window.location;
  if (!hash.startsWith(AUTH_HANDOFF_HASH_PREFIX)) {
    return null;
  }
  const token = decodeURIComponent(hash.slice(AUTH_HANDOFF_HASH_PREFIX.length));
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}`
  );
  return token || null;
};

// Kept deliberately. Nothing reads these keys any more -- the sidenav plan
// badge used to read localStorage.license and always found it missing, which
// is why it showed TRIAL for every tenant -- but users upgrading from an
// older build still have the stale values sitting in storage.
const clearLegacyUserLicenseStorage = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('license');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('license');
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [license, setLicense] = useState(null);
  // Set when the platform has locked this workspace out (suspended, archived
  // or deactivated). Held rather than signing out, because signing out is
  // what made a lockout indistinguishable from an expired session.
  const [tenantLockout, setTenantLockout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(() => getAccessToken());
  const authCheckRef = useRef(null);
  const interceptorsAttachedRef = useRef(false);
  const handleUnauthorizedRef = useRef(null);
  const refreshAccessTokenRef = useRef(null);
  const refreshInFlightRef = useRef(null);
  const isSigningOutRef = useRef(false);
  const isBootstrappingRef = useRef(true);
  const bootstrapGenerationRef = useRef(0);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const timezoneSyncAttemptedRef = useRef(false);

  const navigate = useNavigate();

  const isProd = process.env.NODE_ENV === 'production';
  const baseHost = isProd
    ? (process.env.REACT_APP_FRONTEND || 'localhost').trim().replace(/\/$/, '')
    : 'pearlcrm.localhost';
  const baseURL = getApiBaseUrl();

  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL,
        withCredentials: true,
        paramsSerializer: (params) => qs.stringify(params, { encode: true }),
      }),
    [baseURL]
  );

  const applyAccessToken = useCallback((accessToken) => {
    setAccessToken(accessToken);
    setToken(accessToken);
  }, []);

  const clearSessionData = useCallback(() => {
    setUser(null);
    setLicense(null);
    timezoneSyncAttemptedRef.current = false;
    clearLegacyUserLicenseStorage();
  }, []);

  // The backend rotates the refresh token on every call and treats a replayed
  // token as theft, revoking the whole token family. Concurrent callers must
  // therefore share a single in-flight request instead of each rotating.
  const refreshAccessToken = useCallback(() => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const request = (async () => {
      const response = await axios.post(
        `${baseURL}/token/refresh`,
        {},
        {
          withCredentials: true,
          validateStatus: (status) => status === 200 || status === 401,
        }
      );

      if (response.status === 401) {
        return null;
      }

      const { access_token } = response.data;
      if (access_token) {
        applyAccessToken(access_token);
        return access_token;
      }

      return null;
    })();

    refreshInFlightRef.current = request.finally(() => {
      refreshInFlightRef.current = null;
    });

    return refreshInFlightRef.current;
  }, [applyAccessToken, baseURL]);

  // ------------------------------------------------------------------
  // PROFILE PICTURE ATTACHMENT
  // ------------------------------------------------------------------

  const [attachments, setAttachments] = useState([]);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  // GET first image attachment for the current user
  const getImageAttachment = useCallback((list = attachments) => {
    const imageAttachments = (list || []).filter(
      (a) => a.file_type && a.file_type.startsWith('image/')
    );
    if (!imageAttachments.length) return null;
    return [...imageAttachments].sort((a, b) => b.id - a.id)[0];
  }, [attachments]);

  const fetchAttachments = useCallback(async (userId) => {
    const id = userId || user?.id;
    if (!id) return [];
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: { parent_table: 'users', parent_id: id },
      });
      const next = response.data || [];
      setAttachments(next);
      return next;
    } catch (error) {
      console.error('Error fetching attachments:', error);
      return [];
    }
  }, [axiosInstance, user?.id]);

  const applyAvatarPreview = useCallback(async (attachmentList, fallbackAvatar) => {
    const attachment = getImageAttachment(attachmentList);
    if (!attachment) {
      setImagePreviewUrl('');
      if (fallbackAvatar) {
        setUser((prev) => (prev ? { ...prev, avatar: fallbackAvatar } : prev));
      }
      return fallbackAvatar || '';
    }

    try {
      if (!attachment.bucket_name || !attachment.storage_path) {
        throw new Error('Attachment storage metadata is invalid for strict R2 download');
      }
      const url = await previewPrivateObject({ axiosInstance, attachmentId: attachment.id });
      const preview = url || '';
      setImagePreviewUrl(preview);
      if (preview) {
        setUser((prev) => (prev ? { ...prev, avatar: preview } : prev));
      }
      return preview;
    } catch (error) {
      console.error('Error previewing attachment:', error);
      if (fallbackAvatar) {
        setUser((prev) => (prev ? { ...prev, avatar: fallbackAvatar } : prev));
      }
      return fallbackAvatar || '';
    }
  }, [axiosInstance, getImageAttachment]);

  const refreshUserAvatar = useCallback(async () => {
    if (!user?.id) return '';
    const list = await fetchAttachments(user.id);
    return await applyAvatarPreview(list, user.avatar);
  }, [applyAvatarPreview, fetchAttachments, user?.id, user?.avatar]);

  // Fetch existing attachment when authenticated user is available
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }
    let cancelled = false;
    (async () => {
      const list = await fetchAttachments(user.id);
      if (!cancelled) {
        await applyAvatarPreview(list, user.avatar);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only re-run when auth identity changes — not on every avatar URL update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  // ------------------------------------------------------------------

  useEffect(() => {
    if (!isAuthenticated || !user?.id || user.timezone) return;
    if (timezoneSyncAttemptedRef.current) return;

    const detected = getBrowserTimezone();
    if (!detected) return;
    timezoneSyncAttemptedRef.current = true;

    (async () => {
      try {
        await axiosInstance.patch('/users/me/timezone', { timezone: detected });
        setUser((prev) => (prev ? { ...prev, timezone: detected } : prev));
      } catch (error) {
        console.error('Could not save timezone:', error);
      }
    })();
  }, [isAuthenticated, user?.id, user?.timezone, axiosInstance]);

  // For the settings dropdown and the mismatch alert.
  const setTimezone = useCallback(
      async (timezone) => {
        await axiosInstance.patch('/users/me/timezone', { timezone });
        setUser((prev) => (prev ? { ...prev, timezone } : prev));
      },
      [axiosInstance]
  );

  const signout = useCallback(async () => {
    // Prevent refresh/401 handlers from kicking off a second logout while we
    // clear storage and leave the tenant host.
    if (isSigningOutRef.current) {
      return;
    }
    isSigningOutRef.current = true;

    const accessToken = getAccessToken();

    try {
      // Revokes the refresh-token family and clears the HttpOnly cookie.
      // withCredentials is required so the browser sends (and accepts clearing)
      // the shared .domain cookie that would otherwise re-login on refresh.
      await axios.post(
        `${baseURL}/logout`,
        {},
        {
          withCredentials: true,
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        }
      );
    } catch (logoutError) {
      console.error('Server logout failed:', logoutError);
    }

    clearSessionData();
    clearAccessToken();
    setToken(null);
    clearLegacyTokenStorage();
    setIsAuthenticated(false);

    // Always hard-navigate to the main app login (not the tenant host).
    // A previous check used pathname.startsWith('/'), which matched every
    // route and skipped the redirect — users saw "signed out" on the tenant
    // URL, then a refresh restored the session via the refresh cookie.
    window.location.replace(buildAppLoginUrl({ signedOut: true }));
  }, [baseURL, clearSessionData]);

  const handleUnauthorized = useCallback(() => {
    if (isBootstrappingRef.current || isSigningOutRef.current) {
      return;
    }
    if (!getAccessToken() && !user) {
      return;
    }
    isSigningOutRef.current = true;
    signout();
  }, [signout, user]);

  handleUnauthorizedRef.current = handleUnauthorized;
  refreshAccessTokenRef.current = refreshAccessToken;

  useEffect(() => {
    if (interceptorsAttachedRef.current) {
      return undefined;
    }
    interceptorsAttachedRef.current = true;

    const requestInterceptor = axiosInstance.interceptors.request.use(
      async (config) => {
        const currentToken = getAccessToken();
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => {
        frontendLogger.logError(error, 'Request Interceptor');
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const requestUrl = originalRequest?.url || '';
        const isAuthEndpoint =
          requestUrl.includes('/token') || requestUrl.includes('/verify');

        // Checked before the 401 handling below, and never refreshed or
        // retried: the token is fine, the workspace is not. Retrying would
        // only produce the same 403 again.
        const lockout = getTenantLockout(error);
        if (lockout) {
          setTenantLockout(lockout);
          return Promise.reject(error);
        }

        // Connected inbox OAuth/SMTP failure — not a PearlCRM session expiry.
        if (isEmailProviderAuthFailure(error)) {
          return Promise.reject(error);
        }

        if (
          error.response?.status === 401 &&
          !isAuthEndpoint &&
          !originalRequest?._retry
        ) {
          originalRequest._retry = true;
          try {
            const newToken = await refreshAccessTokenRef.current?.();
            if (newToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axiosInstance(originalRequest);
            }
            if (getAccessToken() || originalRequest.headers?.Authorization) {
              handleUnauthorizedRef.current?.();
            }
          } catch (refreshError) {
            if (getAccessToken() || originalRequest.headers?.Authorization) {
              handleUnauthorizedRef.current?.();
            }
            return Promise.reject(refreshError);
          }
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !isAuthEndpoint) {
          if (getAccessToken() || originalRequest?.headers?.Authorization) {
            handleUnauthorizedRef.current?.();
          }
        } else if (error.message === 'Network Error') {
          console.error('Network error - please check your connection');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
      interceptorsAttachedRef.current = false;
    };
  }, [axiosInstance]);

  const redirectToSubdomain = useCallback(
    (schema, accessToken = null) => {
      const currentHostname = window.location.hostname;
      if (!shouldRedirectToTenantSubdomain(schema, currentHostname)) {
        return;
      }

      const protocol = isProd ? 'https://' : 'http://';
      const port = !isProd && window.location.port ? `:${window.location.port}` : '';
      const handoff = accessToken
        ? `${AUTH_HANDOFF_HASH_PREFIX}${encodeURIComponent(accessToken)}`
        : '';
      const newUrl = `${protocol}${schema}.${baseHost}${port}/dashboard${handoff}`;
      window.location.href = newUrl;
    },
    [baseHost, isProd]
  );

  const signin = useCallback(
    async (username, password, rememberMe) => {
      try {
        // A new attempt supersedes whatever the last one concluded.
        setTenantLockout(null);

        const params = new URLSearchParams();
        params.append('username', username.trim());
        params.append('password', password.trim());
        params.append('remember_me', rememberMe ? 'true' : 'false');

        const response = await axiosInstance.post('/token', params, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          withCredentials: true,
        });

        const { access_token, user_info, license: licenseInfo } = response.data;

        if (!access_token) {
          throw new Error('No access token received');
        }

        clearLegacyTokenStorage();
        clearLegacyUserLicenseStorage();
        applyAccessToken(access_token);

        try {
          const verifyResponse = await axios.post(`${baseURL}/verify`, null, {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
            withCredentials: true,
          });

          const { schema } = verifyResponse.data;
          if (shouldRedirectToTenantSubdomain(schema, window.location.hostname)) {
            // The token travels to the workspace in the #auth= hash only. Kept
            // here, it would outlive sign-out (which runs on the workspace host
            // and cannot clear this one) and sign the user straight back in.
            clearAccessToken();
            redirectToSubdomain(schema, access_token);
            return { success: true, redirecting: true };
          }
          setUser({ ...user_info, avatar: user_info?.avatar || null });
          setLicense(licenseInfo || null);
          setIsAuthenticated(true);
          return { success: true };
        } catch (verifyError) {
          console.error('Verify endpoint failed:', verifyError);
          setUser(user_info);
          setLicense(licenseInfo || null);
          setIsAuthenticated(true);
          return { success: true };
        }
      } catch (error) {
        console.error('Login error:', error);

        if (error.response?.status === 401) {
          throw new Error('Invalid username or password');
        }
        if (error.response?.data?.detail) {
          const { detail } = error.response.data;
          if (Array.isArray(detail)) {
            throw new Error(
              detail.map((item) => item.msg || item.message || JSON.stringify(item)).join(', ')
            );
          }
          throw new Error(typeof detail === 'string' ? detail : 'Authentication failed');
        }
        if (error.message === 'Network Error') {
          throw new Error('Unable to reach the server. Please check your connection and try again.');
        }
        throw new Error('An error occurred during authentication. Please try again.');
      }
    },
    [
      applyAccessToken,
      axiosInstance,
      baseURL,
      navigate,
      redirectToSubdomain,
    ]
  );

  const checkAuth = useCallback(async () => {
    if (authCheckRef.current) {
      return authCheckRef.current;
    }

    const runCheck = async () => {
      const generation = ++bootstrapGenerationRef.current;
      isBootstrappingRef.current = true;
      setIsLoading(true);
      try {
        clearLegacyTokenStorage();
        clearLegacyUserLicenseStorage();

        // Landing here after a sign-out: forget any token this host still
        // holds and stay on the sign-in page.
        if (consumeSignedOutFlag()) {
          clearAccessToken();
          setIsAuthenticated(false);
          setToken(null);
          clearSessionData();
          return false;
        }

        const handoffToken = consumeAuthHandoffFromHash();
        if (handoffToken) {
          applyAccessToken(handoffToken);
        }

        let currentToken = getAccessToken();
        if (!currentToken) {
          currentToken = await refreshAccessToken();
        } else {
          applyAccessToken(currentToken);
        }

        if (!currentToken) {
          setIsAuthenticated(false);
          setToken(null);
          clearSessionData();
          return false;
        }

        const verifySession = async (accessToken) => {
          const response = await axiosInstance.post('/verify');
          const { valid, user: verifiedUser, schema, license: licenseInfo } = response.data;

          if (!valid) {
            throw new Error('Invalid token');
          }

          const userData = {
            ...verifiedUser,
            schema_name: schema ? `tenant_${schema}` : undefined,
          };
          setUser({ ...userData, avatar: verifiedUser?.avatar || null });
          setLicense(licenseInfo || null);
          setIsAuthenticated(true);
          setToken(currentToken);

          if (shouldRedirectToTenantSubdomain(schema, window.location.hostname)) {
            clearAccessToken();
            redirectToSubdomain(schema, accessToken);
            return true;
          }

          if (window.location.pathname === '/auth/login') {
            navigate('/dashboard', { replace: true });
          }

          return true;
        };

        try {
          return await verifySession(currentToken);
        } catch (verifyError) {
          const refreshedToken = await refreshAccessToken();
          if (!refreshedToken) {
            throw verifyError;
          }
          return await verifySession(refreshedToken);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setToken(null);
        clearSessionData();
        clearAccessToken();
        return false;
      } finally {
        if (generation === bootstrapGenerationRef.current) {
          isBootstrappingRef.current = false;
          setIsAuthReady(true);
          setIsLoading(false);
        }
      }
    };

    authCheckRef.current = runCheck();
    try {
      return await authCheckRef.current;
    } finally {
      authCheckRef.current = null;
    }
  }, [applyAccessToken, axiosInstance, clearSessionData, navigate, redirectToSubdomain, refreshAccessToken]);

  useEffect(() => {
    checkAuth();
    // Mount-only session bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      license,
      tenantLockout,
      clearTenantLockout: () => setTenantLockout(null),
      signin,
      signout,
      axiosInstance,
      isAuthenticated,
      isLoading,
      isAuthReady,
      checkAuth,
      token,
      refreshUserAvatar,
      updateUserAvatar: (avatarUrl) => {
        if (avatarUrl) {
          setImagePreviewUrl(avatarUrl);
          setUser((prev) => (prev ? { ...prev, avatar: avatarUrl } : prev));
        }
      },
      // Effective zone: saved choice -> device -> UTC.
      // The device fallback only applies for the moment before the first save lands.
      timezone: user?.timezone || getBrowserTimezone() || 'UTC',
      setTimezone,
    }),
    [user, license, tenantLockout, signin, signout, axiosInstance, isAuthenticated, isLoading, isAuthReady, checkAuth, token, refreshUserAvatar, setTimezone]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
